'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { RiMenu3Fill } from 'react-icons/ri';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'line-clamp-1 flex w-full flex-1 flex-row items-center justify-between overflow-hidden px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'h-7 text-left text-xl font-bold',
              layer === 1 && 'h-6 text-[16px] text-lg font-semibold',
              layer && layer > 1 && 'h-6 text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger aria-label={category.name} className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  const categories = props.categories || generalLayout.header?.categories;

  const contactInfo = [
    {
      icon: '/images/icons/phone.svg',
      title: generalLayout?.contact?.phone,
      link: `tel:${generalLayout?.contact?.phone}`,
      text: 'Zavolajte mi na telefón',
    },
    {
      icon: '/images/icons/mail.svg',
      title: generalLayout?.contact?.email,
      link: `mailto:${generalLayout?.contact?.email}`,
      text: 'Napíšte mi email',
    },
    {
      icon: '/images/icons/clock.svg',
      title: 'Pon - Pia: 8:00 - 18:00',
      text: 'Som k dispozícii',
    },
  ];

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Menu"
          className={cn('text-secondary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <RiMenu3Fill size={24} />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={'right'}
          className={cn(
            'justify-between overflow-scroll p-0',
            'ea-mobile-sidebar__content',
            {
              'pt-20': !props?.firstSeparator,
            },
            props.className?.content,
          )}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          {!!props.menu && (
            <div className="flex flex-col items-center justify-center gap-4">
              {props.menu?.map((item, index) => {
                const Comp = item.path
                  ? isRelativeHref(getSafeHref(item.path))
                    ? Link
                    : 'a'
                  : 'span';
                return (
                  <SheetClose key={index} asChild>
                    <Comp
                      href={item.path ? getSafeHref(item.path) : (undefined as any)}
                      className="flex w-full"
                      prefetch={false}
                    >
                      <div
                        className={cn('text-center text-2xl font-bold text-secondary', 'mx-auto')}
                      >
                        {item.name}
                      </div>
                    </Comp>
                  </SheetClose>
                );
              })}
            </div>
          )}
          <Separator />
          {contactInfo?.map((item, index) => (
            <div key={index} className={cn('', 'ea-custom-footer__contact-item--sidebar')}>
              <Image src={item.icon} width={32} height={32} alt={item?.text} />
              <div className="flex flex-col items-center justify-center space-y-1">
                <strong className="mx-auto text-center">{item?.title}</strong>
                <p className={cn('gradient-text')}>{item?.text}</p>
              </div>
            </div>
          ))}
          <Button asChild>
            <Link prefetchOnHover href={'#kontakt'}>{t('text1243')}</Link>
          </Button>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;


